import React, { useEffect, useRef, useState } from 'react';
import { Autocomplete } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import FormLabel from '@mui/material/FormLabel';
import InputAdornment from '@mui/material/InputAdornment';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import TextField from '@mui/material/TextField';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useAtom, useSetAtom } from 'jotai';
import useDebounce from '@/hooks/useDebounce';
import { useFormControls } from '@/hooks/useFormControls';
import { contactDemandeState, diagnosticParcellesState, envContraintesState } from '@/store';
import { getCommunes } from '@/utils/locationApi';

const INPUT_FIELDS_VALUES = [
  {
    name: 'civilite',
    required: true,
  },
  {
    name: 'nom',
    required: true,
  },
  {
    name: 'prenom',
    required: true,
  },
  {
    name: 'telephone',
    required: true,
  },
  {
    name: 'email',
    type: 'email',
    required: true,
  },
  {
    name: 'commentaire',
    required: false,
  },
];

export default function ContactFormDialog() {
  const ref = useRef();

  const [open, setOpen] = useState(false);

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  const [acceptGCU, setAcceptGCU] = useState(false);
  const [canSubmit, setCanSubmit] = useState(false);

  const [diagnosticParcelles, setDiagnosticParcelles] = useAtom(diagnosticParcellesState);

  const setEnvContraintes = useSetAtom(envContraintesState);
  const setContactDemande = useSetAtom(contactDemandeState);

  const [selectedCommune, setSelectedCommune] = useState(null);

  useEffect(() => {
    if (diagnosticParcelles) {
      setOpen(true);
    }
  }, [diagnosticParcelles]);

  const { handleInputValue, isValid, formErrors, formValues, resetForm } = useFormControls(INPUT_FIELDS_VALUES);

  useEffect(() => {
    if (!open) {
      return;
    }

    setCanSubmit(acceptGCU && isValid() && (diagnosticParcelles.parcelles.length > 0 || selectedCommune));
  }, [isValid, acceptGCU, open, diagnosticParcelles, selectedCommune]);

  const handleDialogClose = () => {
    setOpen(false);
    setEnvContraintes(null);
    setDiagnosticParcelles(null);
    setAcceptGCU(false);
    resetForm();

    if (selectedCommune) {
      setSelectedCommune(null);
      setCommuneInput('');
    }
  };

  const handleFormSubmit = async () => {
    setContactDemande({
      contact: formValues,
      commune_parcelle: selectedCommune && {
        nom: selectedCommune.properties?.city,
        code_postal: selectedCommune.properties?.postcode,
      },
      parcelles_list: diagnosticParcelles.parcelles,
      contraintes: diagnosticParcelles.contraintes,
    });

    setOpen(false);
    setAcceptGCU(false);
    resetForm();
  };

  const onGCUChange = ({ target }) => {
    setAcceptGCU(target.checked);
  };

  const [communeSearchTerm, setCommuneSearchTerm] = useState('');

  const [communes, setCommunes] = useState([]);

  const [isCommuneSearching, setIsCommuneSearching] = useState(false);

  const [communeInput, setCommuneInput] = useState('');

  // Debounce search term
  const debouncedCommuneSearchTerm = useDebounce(communeSearchTerm, 500);

  const getCommuneFeatureDescription = (commune) => {
    const communeProps = commune?.properties;
    const dept = communeProps?.context?.split(',')[0];
    return `${communeProps?.city} (${dept})`;
  };

  useEffect(() => {
    async function searchCommune() {
      setIsCommuneSearching(true);
      const results = await getCommunes(debouncedCommuneSearchTerm);
      setCommunes(results?.data?.features);
      setIsCommuneSearching(false);
    }

    if (debouncedCommuneSearchTerm && debouncedCommuneSearchTerm.length > 2) {
      searchCommune();
    } else {
      setCommunes([]);
      setIsCommuneSearching(false);
    }
  }, [debouncedCommuneSearchTerm]); // Only call effect if debounced search term changes

  const onInputCommuneChange = (e) => {
    let userInputText = e.target.value;
    if (!userInputText) {
      userInputText = '';
    }

    setCommuneSearchTerm(userInputText);
    setCommuneInput(userInputText);
  };

  const onCommuneChange = async (e, adresse) => {
    setSelectedCommune(adresse);

    if (!adresse) {
      return;
    }

    setCommuneInput(getCommuneFeatureDescription(adresse));
    setCommunes([]);
  };

  return (
    <div ref={ref}>
      <Dialog
        container={ref.current}
        maxWidth="md"
        fullWidth={true}
        scroll="paper"
        open={open}
        fullScreen={fullScreen}
        onClose={null}
        aria-labelledby="responsive-dialog-title"
      >
        <DialogTitle id="responsive-dialog-title" color="primary">
          Validation de votre projet
        </DialogTitle>
        <DialogContent dividers={true}>
          <DialogContentText align="justify">
            Complétez le formulaire ci-dessous pour nous transmettre vos coordonnées. Vos informations cadastrales nous
            seront alors transmises automatiquement. Nous vous recontacterons prochainement pour échanger sur votre
            projet.
          </DialogContentText>
          <Box
            sx={{
              pt: 1,
              width: '100%',
            }}
          >
            <FormControl required sx={{ flexDirection: 'row', mt: 1 }}>
              <FormLabel id="civilite-label" sx={{ display: 'inline-flex', alignItems: 'center', mr: 2 }}>
                Civilité
              </FormLabel>
              <RadioGroup aria-labelledby="civilite-label" name="civilite" onChange={(e) => handleInputValue(e)} row>
                <FormControlLabel value="Madame" control={<Radio />} label="Madame" />
                <FormControlLabel value="Monsieur" control={<Radio />} label="Monsieur" />
              </RadioGroup>
            </FormControl>
            <TextField
              required
              fullWidth
              key="2"
              slotProps={{ htmlInput: { maxLength: 64 } }}
              onBlur={(e) => handleInputValue(e)}
              onChange={(e) => handleInputValue(e)}
              name="nom"
              label="Nom"
              autoComplete="off"
              variant="outlined"
              margin="dense"
              {...(formErrors['nom'] && {
                error: true,
                helperText: formErrors['nom'],
              })}
            />
            <TextField
              required
              fullWidth
              key="3"
              slotProps={{ htmlInput: { maxLength: 64 } }}
              onBlur={(e) => handleInputValue(e)}
              onChange={(e) => handleInputValue(e)}
              name="prenom"
              label="Prénom"
              autoComplete="off"
              variant="outlined"
              margin="dense"
              {...(formErrors['prenom'] && {
                error: true,
                helperText: formErrors['prenom'],
              })}
            />
            <TextField
              required
              fullWidth
              key="4"
              slotProps={{ htmlInput: { maxLength: 16 } }}
              onBlur={(e) => handleInputValue(e)}
              onChange={(e) => handleInputValue(e)}
              name="telephone"
              label="Téléphone"
              autoComplete="off"
              variant="outlined"
              margin="dense"
              {...(formErrors['telephone'] && {
                error: true,
                helperText: formErrors['telephone'],
              })}
            />
            <TextField
              required
              fullWidth
              key="5"
              slotProps={{ htmlInput: { maxLength: 120 } }}
              onBlur={(e) => handleInputValue(e)}
              onChange={(e) => handleInputValue(e)}
              name="email"
              label="E-mail"
              autoComplete="off"
              variant="outlined"
              margin="dense"
              {...(formErrors['email'] && {
                error: true,
                helperText: formErrors['email'],
              })}
            />
            <TextField
              fullWidth
              key="6"
              slotProps={{ htmlInput: { maxLength: 1000 } }}
              onBlur={(e) => handleInputValue(e)}
              onChange={(e) => handleInputValue(e)}
              name="commentaire"
              label="Besoin de préciser votre projet ? Zone d'expression libre"
              multiline
              autoComplete="off"
              variant="outlined"
              margin="dense"
              rows={3}
              {...(formErrors['commentaire'] && {
                error: true,
                helperText: formErrors['commentaire'],
              })}
            />
            {diagnosticParcelles?.parcelles.length === 0 && (
              <Autocomplete
                freeSolo
                required
                options={communes}
                getOptionLabel={(option) => getCommuneFeatureDescription(option)}
                inputValue={communeInput}
                filterOptions={(options) => options}
                onInputChange={onInputCommuneChange}
                onChange={onCommuneChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    label="Commune de localisation de la parcelle"
                    variant="outlined"
                    margin="dense"
                    slotProps={{
                      ...params.slotProps,
                      input: {
                        ...params.slotProps.input,
                        endAdornment: (
                          <>
                            {isCommuneSearching && (
                              <InputAdornment position="end">
                                <CircularProgress size={20} />
                              </InputAdornment>
                            )}
                            {params.slotProps.input.endAdornment}
                          </>
                        ),
                      },
                    }}
                  />
                )}
              />
            )}
          </Box>
          <DialogContentText>* Champ obligatoire</DialogContentText>
          <Box
            sx={{
              mt: 2,
              border: 1,
              borderColor: 'grey.400',
              borderRadius: 1,
              p: 2,
            }}
          >
            <FormGroup>
              <FormControlLabel
                control={<Checkbox onChange={onGCUChange} />}
                label={
                  <DialogContentText align="justify">
                    En validant ce formulaire, j&apos;accepte que mes données personnelles soient utilisées pour le
                    suivi de ma demande, les échanges par e-mail, par téléphone, et en cas de besoin pour la relation
                    contractuelle qui peut en resulter. Voir page politique de confidentialité.
                  </DialogContentText>
                }
              />
            </FormGroup>
          </Box>
        </DialogContent>
        <DialogActions sx={{ mr: 2 }}>
          <Button variant="contained" disabled={!canSubmit} onClick={handleFormSubmit}>
            Envoyer
          </Button>
          <Button variant="outlined" onClick={handleDialogClose}>
            Annuler
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
